/**
 * lp-wishlist.js — single source of truth for the "favourites / wishlist" hearts.
 * ---------------------------------------------------------------------------
 * Loaded globally from theme.liquid so every section (product grid, collection
 * grid, carousel, collage, predictive search, PDP) and the /pages/wishlist page
 * share one store.
 *
 * Storage (localStorage, per browser — no backend):
 *   key:   cpc_wishlist_<shop>
 *   value: { "<productId>": { id, handle, url, title, image, price, addedAt } }
 *
 * Legacy data is migrated on first read:
 *   - old value shape { "<id>": true }              (grids / carousel / collage)
 *   - old "lp-favourites" array of ids              (predictive search)
 *
 * Public API — window.LPWishlist:
 *   .all()            -> array of entries, newest first
 *   .ids()            -> array of id strings
 *   .has(id)          -> boolean
 *   .count()          -> number
 *   .toggle(id, meta) -> boolean (the new pressed state)
 *   .add(id, meta)    -> void
 *   .remove(id)       -> void
 *   .clear()          -> void
 *   .subscribe(fn)    -> unsubscribe fn   (also fires the `lp:wishlist:change`
 *                        event on `document` for non-JS listeners)
 */
(function () {
  'use strict';

  var SHOP = (window.Shopify && window.Shopify.shop) || 'default';
  var KEY = 'cpc_wishlist_' + SHOP;
  var LEGACY_ARRAY_KEY = 'lp-favourites';
  var listeners = [];

  function safeParse(raw, fallback) {
    try {
      var v = JSON.parse(raw);
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  function normalizeEntry(id, value) {
    var base = { id: String(id), handle: '', url: '', title: '', image: '', price: null, addedAt: 0 };
    if (value && typeof value === 'object') {
      base.handle = value.handle || '';
      base.url = value.url || '';
      base.title = value.title || '';
      base.image = value.image || '';
      base.price = value.price != null ? value.price : null;
      base.addedAt = value.addedAt || 0;
    }
    return base;
  }

  /** Read + migrate. Always returns a plain object keyed by id string. */
  function read() {
    var store = safeParse(localStorage.getItem(KEY), {});
    if (Array.isArray(store)) {
      // very old shape: array of ids
      var obj = {};
      store.forEach(function (id) { obj[String(id)] = normalizeEntry(id, null); });
      store = obj;
    }

    var changed = false;

    // Migrate `true` values -> full entries (with whatever we know, i.e. nothing).
    Object.keys(store).forEach(function (id) {
      if (store[id] === true || store[id] == null) {
        store[id] = normalizeEntry(id, null);
        changed = true;
      } else {
        store[id] = normalizeEntry(id, store[id]);
      }
    });

    // One-time merge of the old predictive-search key.
    var legacy = safeParse(localStorage.getItem(LEGACY_ARRAY_KEY), null);
    if (Array.isArray(legacy) && legacy.length) {
      legacy.forEach(function (id) {
        id = String(id);
        if (!store[id]) { store[id] = normalizeEntry(id, null); changed = true; }
      });
      try { localStorage.removeItem(LEGACY_ARRAY_KEY); } catch (e) {}
    }

    if (changed) write(store, true);
    return store;
  }

  function write(store, silent) {
    try {
      localStorage.setItem(KEY, JSON.stringify(store));
    } catch (e) {
      /* private mode / quota — session only */
    }
    if (!silent) emit(store);
  }

  function emit(store) {
    var entries = toArray(store || read());
    listeners.slice().forEach(function (fn) {
      try { fn(entries); } catch (e) {}
    });
    try {
      document.dispatchEvent(
        new CustomEvent('lp:wishlist:change', { detail: { entries: entries } })
      );
    } catch (e) {}
  }

  function toArray(store) {
    return Object.keys(store)
      .map(function (id) { return store[id]; })
      .sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });
  }

  var API = {
    key: KEY,

    all: function () { return toArray(read()); },

    ids: function () { return Object.keys(read()); },

    has: function (id) { return Object.prototype.hasOwnProperty.call(read(), String(id)); },

    count: function () { return Object.keys(read()).length; },

    add: function (id, meta) {
      id = String(id);
      if (!id) return;
      var store = read();
      var existing = store[id] || {};
      store[id] = normalizeEntry(id, {
        handle: (meta && meta.handle) || existing.handle,
        url: (meta && meta.url) || existing.url,
        title: (meta && meta.title) || existing.title,
        image: (meta && meta.image) || existing.image,
        price: meta && meta.price != null ? meta.price : existing.price,
        addedAt: existing.addedAt || Date.now()
      });
      write(store);
    },

    remove: function (id) {
      id = String(id);
      var store = read();
      if (store[id]) { delete store[id]; write(store); }
    },

    toggle: function (id, meta) {
      if (API.has(id)) { API.remove(id); return false; }
      API.add(id, meta);
      return true;
    },

    clear: function () { write({}); },

    /**
     * Pull product meta for a heart button. Prefers a `data-wishlist` JSON blob
     * on the button (or an ancestor); falls back to scraping the card DOM so the
     * older markup keeps working.
     */
    meta: function (btn) {
      if (!btn) return {};
      var host = btn.closest('[data-wishlist]');
      if (host) {
        var parsed = safeParse(host.getAttribute('data-wishlist'), null);
        if (parsed && typeof parsed === 'object') return parsed;
      }
      var card = btn.closest(
        '.cpc-card__inner, .pg-card__inner, .pcoll__card, [data-product-id]'
      );
      if (!card) return {};
      var link = card.querySelector('a[href*="/products/"]') || card.querySelector('a[href]');
      var img = card.querySelector('img');
      var titleEl = card.querySelector(
        '.cpc-card__title, .pg-card__title, .pcoll__card-title, [class*="title"]'
      );
      var priceEl = card.querySelector(
        '.cpc-price, .pg-price, .pcoll__card-price, [class*="price"]'
      );
      var url = link ? link.getAttribute('href') : '';
      var handle = '';
      var m = url && url.match(/\/products\/([^/?#]+)/);
      if (m) handle = m[1];
      return {
        handle: handle,
        url: url ? url.split('?')[0] : '',
        title: titleEl ? titleEl.textContent.trim() : '',
        image: img ? img.getAttribute('src') || '' : '',
        price: priceEl ? priceEl.textContent.trim() : null
      };
    },

    subscribe: function (fn) {
      if (typeof fn !== 'function') return function () {};
      listeners.push(fn);
      return function () {
        var i = listeners.indexOf(fn);
        if (i > -1) listeners.splice(i, 1);
      };
    }
  };

  // Keep tabs / windows in sync.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) emit();
  });

  window.LPWishlist = API;

  // Run the migration once on load so legacy keys are cleaned up early.
  read();
})();
